import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
  Divider,
  Tooltip,
} from '@mui/material';
import { Add, Delete, Close, Edit } from '@material-ui/icons';

const NODE_TYPES = [
  { value: 'flat_rate', label: 'Flat Rate', hint: 'Fixed premium amount or fact-ref/formula' },
  { value: 'multi_factor_rating', label: 'Multi-Factor Rating', hint: 'Base + rating table adjustments' },
  { value: 'waiting_period_flat', label: 'Waiting Period (Flat)', hint: 'Fixed waiting period cardinality' },
  { value: 'waiting_period_rating', label: 'Waiting Period (Rating)', hint: 'Base waiting period + table adjustments' },
];

const APPLIES_TO = [
  { value: 'cover', label: 'Cover' },
  { value: 'premium', label: 'Premium' },
  { value: 'waiting_period', label: 'Waiting Period' },
];

const PREMIUM_TYPES = ['flat_rate', 'multi_factor_rating'];
const WAITING_TYPES = ['waiting_period_flat', 'waiting_period_rating'];
const TABLE_TYPES = ['multi_factor_rating', 'waiting_period_rating'];

const GRANULARITIES = ['Day', 'Week', 'Month', 'Year'];

const nodeTypeLabel = (type) => (NODE_TYPES.find(n => n.value === type) || {}).label || type;
const appliesToLabel = (v) => (APPLIES_TO.find(a => a.value === v) || {}).label || v;

const nodeTypeColor = (type) => {
  if (PREMIUM_TYPES.includes(type)) return '#173E4B';
  if (WAITING_TYPES.includes(type)) return '#7B4F12';
  return '#555';
};

const appliesColor = {
  cover: { bg: '#EFF6FF', text: '#1D4ED8' },
  premium: { bg: '#F0FDF4', text: '#15803D' },
  waiting_period: { bg: '#FFF7ED', text: '#C2410C' },
};

const emptyForm = {
  benefit_code: '',
  package_code: '',
  label: '',
  applies_to: '',
  node_type: 'flat_rate',
  base_amount_in_cents: '',
  base_value_ref: '',
  base_formula: '',
  waiting_cardinality: '',
  waiting_granularity: 'Day',
  tables: [],
};

// Prefill the shared form from an existing node (edit mode).
const nodeToForm = (node) => ({
  benefit_code: node.benefit_code || '',
  package_code: node.package_code || '',
  label: node.label || '',
  applies_to: node.applies_to || '',
  node_type: node.node_type || 'flat_rate',
  base_amount_in_cents: node.base_amount_in_cents != null ? String(node.base_amount_in_cents) : '',
  base_value_ref: node.base_value_ref || '',
  base_formula: node.base_formula || '',
  waiting_cardinality: node.base_waiting_period ? String(node.base_waiting_period.cardinality) : '',
  waiting_granularity: (node.base_waiting_period && node.base_waiting_period.granularity) || 'Day',
  tables: (node.tables || []).map(t => ({
    rating_table_guid: t.rating_table_guid,
    order_index: t.order_index != null ? t.order_index : 0,
  })),
});

const RatingNodesTab = (props) => {
  const {
    ratingNodes = [],
    ratingTables = [],
    isLoading = false,
    isSuccess = false,
    isError = null,
    onCreateRatingNode,
    onUpdateRatingNode,
    onDeleteRatingNode,
    resetSuccess,
    resetError,
  } = props;

  const [dialogMode, setDialogMode] = useState(null); // 'create' | 'edit' | null
  const [editGuid, setEditGuid] = useState(null);
  const [deleteConfirmGuid, setDeleteConfirmGuid] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [formErrors, setFormErrors] = useState({});

  const tableLabel = (guid) => {
    const t = ratingTables.find(x => x.guid === guid);
    return t ? (t.name || t.code || guid) : guid;
  };

  useEffect(() => {
    if (isSuccess && dialogMode) {
      setDialogMode(null);
      setEditGuid(null);
      setForm(emptyForm);
      setFormErrors({});
      if (resetSuccess) resetSuccess();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess]);

  const openCreate = () => {
    setForm(emptyForm);
    setFormErrors({});
    setEditGuid(null);
    setDialogMode('create');
  };

  const openEdit = (node) => {
    setForm(nodeToForm(node));
    setFormErrors({});
    setEditGuid(node.guid);
    setDialogMode('edit');
  };

  const closeDialog = () => {
    setDialogMode(null);
    setEditGuid(null);
    setForm(emptyForm);
    setFormErrors({});
    if (isError && resetError) resetError();
  };

  const change = (field) => (event) => {
    // React 16 pools SyntheticEvents — event.target is nulled out by the time
    // this reaches setForm's updater if read there instead of eagerly here.
    const value = event.target.value;
    setForm(prev => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors(prev => { const n = { ...prev }; delete n[field]; return n; });
    }
  };

  // ── table-ref editor ────────────────────────────────────────────────────────
  const addTableRef = () =>
    setForm(prev => ({
      ...prev,
      tables: prev.tables.concat({ rating_table_guid: '', order_index: prev.tables.length }),
    }));
  const setTableRef = (i, patch) =>
    setForm(prev => ({
      ...prev,
      tables: prev.tables.map((t, idx) => (idx === i ? { ...t, ...patch } : t)),
    }));
  const rmTableRef = (i) =>
    setForm(prev => ({ ...prev, tables: prev.tables.filter((_, idx) => idx !== i) }));

  const isPremiumType = PREMIUM_TYPES.includes(form.node_type);
  const isWaitingType = WAITING_TYPES.includes(form.node_type);
  const usesTables = TABLE_TYPES.includes(form.node_type);
  const baseSourceCount = [form.base_amount_in_cents, form.base_value_ref, form.base_formula]
    .filter(v => v !== '' && v !== null && v !== undefined).length;

  const validate = () => {
    const errors = {};
    if (dialogMode === 'create' && !form.benefit_code.trim()) {
      errors.benefit_code = 'Benefit/add-on code is required';
    }
    if (!form.node_type) errors.node_type = 'Node type is required';
    if (isPremiumType) {
      if (baseSourceCount === 0) errors.base_amount_in_cents = 'Provide a base amount, value ref, or formula';
      else if (baseSourceCount > 1) errors.base_amount_in_cents = 'Provide only one of: base amount, value ref, or formula';
    }
    if (isWaitingType) {
      if (form.waiting_cardinality === '' || form.waiting_cardinality === null) {
        errors.waiting_cardinality = 'Waiting period cardinality is required';
      } else if (parseInt(form.waiting_cardinality) < 0) {
        errors.waiting_cardinality = 'Cardinality cannot be negative';
      }
    }
    if (form.tables.some(t => !t.rating_table_guid)) {
      errors.tables = 'Every linked table row must select a table (or remove it)';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Build the mutable part of the payload shared by create + update.
  const buildBody = () => {
    const body = { node_type: form.node_type };
    if (form.label.trim()) body.label = form.label.trim();
    if (form.applies_to) body.applies_to = form.applies_to;
    if (isPremiumType) {
      if (form.base_formula.trim()) body.base_formula = form.base_formula.trim();
      else if (form.base_value_ref.trim()) body.base_value_ref = form.base_value_ref.trim();
      else if (form.base_amount_in_cents !== '') body.base_amount_in_cents = parseInt(form.base_amount_in_cents) || 0;
    }
    if (isWaitingType) {
      body.base_waiting_period = {
        cardinality: parseInt(form.waiting_cardinality) || 0,
        granularity: form.waiting_granularity,
      };
    }
    if (usesTables) {
      body.tables = form.tables.map((t, i) => ({
        rating_table_guid: t.rating_table_guid,
        order_index: t.order_index != null ? Number(t.order_index) : i,
      }));
    }
    return body;
  };

  const submit = () => {
    if (!validate()) return;
    const body = buildBody();
    if (dialogMode === 'edit') {
      if (onUpdateRatingNode) onUpdateRatingNode(editGuid, body);
    } else {
      const payload = { ...body, benefit_code: form.benefit_code.trim() };
      if (form.package_code.trim()) payload.package_code = form.package_code.trim();
      if (onCreateRatingNode) onCreateRatingNode(payload);
    }
  };

  const doDelete = () => {
    if (onDeleteRatingNode && deleteConfirmGuid) onDeleteRatingNode(deleteConfirmGuid);
    setDeleteConfirmGuid(null);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>Rating Nodes</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={openCreate}
          sx={{ textTransform: 'none', backgroundColor: '#173E4B', '&:hover': { backgroundColor: '#0f2d38' } }}
        >
          Add Rating Node
        </Button>
      </Box>

      {isError && <Alert severity="error" sx={{ mb: 2 }} onClose={resetError}>{isError}</Alert>}
      {isSuccess && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={resetSuccess}>
          Rating node operation completed successfully.
        </Alert>
      )}

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress sx={{ color: '#173E4B' }} />
        </Box>
      )}

      {!isLoading && ratingNodes.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
          <Typography variant="body1">No rating nodes yet.</Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Add rating nodes to define how premiums, cover and waiting periods are calculated.
          </Typography>
        </Box>
      )}

      {!isLoading && ratingNodes.length > 0 && (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))', gap: 2 }}>
          {ratingNodes.map(node => (
            <Card key={node.guid} variant="outlined">
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#173E4B', pr: 1 }}>
                    {node.label || node.benefit_code}
                  </Typography>
                  <Box sx={{ display: 'flex', flexShrink: 0 }}>
                    <Tooltip title="Edit node">
                      <IconButton size="small" onClick={() => openEdit(node)} sx={{ color: '#173E4B' }}>
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete node">
                      <IconButton size="small" onClick={() => setDeleteConfirmGuid(node.guid)} sx={{ color: '#c62828' }}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', alignItems: 'center', mb: 1 }}>
                  <Chip label={nodeTypeLabel(node.node_type)} size="small"
                    sx={{ backgroundColor: nodeTypeColor(node.node_type), color: '#fff', fontSize: 11 }} />
                  {node.applies_to && (() => {
                    const c = appliesColor[node.applies_to] || { bg: '#F3F4F6', text: '#374151' };
                    return <Chip label={appliesToLabel(node.applies_to)} size="small"
                      sx={{ height: 22, fontSize: 11, fontWeight: 600, bgcolor: c.bg, color: c.text }} />;
                  })()}
                  <Chip label={node.active === false ? 'Inactive' : 'Active'} size="small"
                    color={node.active === false ? 'default' : 'success'} sx={{ height: 22 }} />
                </Box>

                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {node.benefit_code}{node.package_code ? ` · pkg ${node.package_code}` : ' · add-on'}
                </Typography>

                <Divider sx={{ my: 1.25 }} />

                {node.base_formula && (
                  <Typography variant="body2" sx={{ mb: 0.5 }}><strong>Formula:</strong> <code>{node.base_formula}</code></Typography>
                )}
                {node.base_value_ref && (
                  <Typography variant="body2" sx={{ mb: 0.5 }}><strong>Value ref:</strong> <code>{node.base_value_ref}</code></Typography>
                )}
                {node.base_amount_in_cents != null && !node.base_formula && !node.base_value_ref && (
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>Base:</strong> {node.base_amount_in_cents.toLocaleString()} cents
                  </Typography>
                )}
                {node.base_waiting_period && (
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>Waiting period:</strong> {node.base_waiting_period.cardinality} {node.base_waiting_period.granularity}(s)
                  </Typography>
                )}

                {/* Linked tables — names, not just a count */}
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    Linked tables ({(node.tables || []).length})
                  </Typography>
                  {(node.tables || []).length === 0 ? (
                    <Typography variant="body2" sx={{ color: '#9CA3AF' }}>none — base value only</Typography>
                  ) : (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                      {(node.tables || [])
                        .slice()
                        .sort((a, b) => (a.order_index || 0) - (b.order_index || 0))
                        .map((t, i) => (
                          <Chip
                            key={t.rating_table_guid + '-' + i}
                            label={`${i + 1}. ${tableLabel(t.rating_table_guid)}`}
                            size="small"
                            variant="outlined"
                            sx={{ height: 22, fontSize: 11, borderColor: '#CBD5E1' }}
                          />
                        ))}
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      {/* Create / Edit dialog */}
      <Dialog open={Boolean(dialogMode)} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {dialogMode === 'edit' ? 'Edit Rating Node' : 'Add Rating Node'}
          </Typography>
          <IconButton onClick={closeDialog}><Close /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 1 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <TextField
                label="Benefit / Add-on Code *"
                value={form.benefit_code}
                onChange={change('benefit_code')}
                error={Boolean(formErrors.benefit_code)}
                helperText={formErrors.benefit_code || (dialogMode === 'edit' ? 'Immutable' : 'e.g. life_cover')}
                size="small"
                disabled={dialogMode === 'edit'}
              />
              <TextField
                label="Package Code (optional)"
                value={form.package_code}
                onChange={change('package_code')}
                helperText={dialogMode === 'edit' ? 'Immutable' : 'Blank for add-on nodes'}
                size="small"
                disabled={dialogMode === 'edit'}
              />
            </Box>

            <TextField
              label="Label"
              value={form.label}
              onChange={change('label')}
              size="small"
              placeholder="e.g. Gold Package Premium"
              helperText="Human-readable name shown on the card"
            />

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Node Type *</InputLabel>
                <Select value={form.node_type} label="Node Type *" onChange={change('node_type')} error={Boolean(formErrors.node_type)}>
                  {NODE_TYPES.map(t => (
                    <MenuItem key={t.value} value={t.value}>
                      <Box>
                        <Typography variant="body2">{t.label}</Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>{t.hint}</Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth size="small">
                <InputLabel>Applies To</InputLabel>
                <Select value={form.applies_to} label="Applies To" onChange={change('applies_to')} displayEmpty>
                  <MenuItem value=""><em>Unset</em></MenuItem>
                  {APPLIES_TO.map(a => <MenuItem key={a.value} value={a.value}>{a.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Box>

            {isPremiumType && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1.5, color: 'text.secondary' }}>Base source — provide exactly one:</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <TextField label="Base Amount (cents)" type="number" value={form.base_amount_in_cents}
                    onChange={change('base_amount_in_cents')} disabled={Boolean(form.base_value_ref || form.base_formula)} size="small"
                    error={Boolean(formErrors.base_amount_in_cents) && baseSourceCount === 0}
                    helperText={formErrors.base_amount_in_cents || ''} />
                  <TextField label="Value Ref" value={form.base_value_ref} onChange={change('base_value_ref')}
                    disabled={Boolean(form.base_amount_in_cents || form.base_formula)} size="small"
                    placeholder="quote.sum_insured_in_cents" helperText="Fact path resolved at eval time" />
                  <TextField label="Formula" value={form.base_formula} onChange={change('base_formula')}
                    disabled={Boolean(form.base_amount_in_cents || form.base_value_ref)} size="small"
                    placeholder="0.05 * quote.loan_amount_in_cents" helperText='coefficient * fact_ref' />
                </Box>
              </Box>
            )}

            {isWaitingType && (
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <TextField label="Waiting Cardinality *" type="number" value={form.waiting_cardinality}
                  onChange={change('waiting_cardinality')} error={Boolean(formErrors.waiting_cardinality)}
                  helperText={formErrors.waiting_cardinality} size="small" />
                <FormControl fullWidth size="small">
                  <InputLabel>Granularity</InputLabel>
                  <Select value={form.waiting_granularity} label="Granularity" onChange={change('waiting_granularity')}>
                    {GRANULARITIES.map(g => <MenuItem key={g} value={g}>{g}</MenuItem>)}
                  </Select>
                </FormControl>
              </Box>
            )}

            {/* Linked tables editor — for multi-factor / waiting-period-rating nodes */}
            {usesTables && (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
                    Linked rating tables (folded in order)
                  </Typography>
                  <Button size="small" startIcon={<Add />} onClick={addTableRef} sx={{ textTransform: 'none' }}>Add table</Button>
                </Box>
                {formErrors.tables && <Alert severity="error" sx={{ mb: 1 }}>{formErrors.tables}</Alert>}
                {form.tables.length === 0 && (
                  <Typography variant="body2" sx={{ color: '#9CA3AF' }}>No tables linked — base value is used alone.</Typography>
                )}
                {form.tables.map((t, i) => (
                  <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Table</InputLabel>
                      <Select value={t.rating_table_guid} label="Table"
                        onChange={e => setTableRef(i, { rating_table_guid: e.target.value })}>
                        {ratingTables.length === 0 && <MenuItem value="" disabled>No rating tables for this partner</MenuItem>}
                        {ratingTables.map(rt => (
                          <MenuItem key={rt.guid} value={rt.guid}>{rt.name || rt.code || rt.guid}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <TextField label="Order" type="number" size="small" value={t.order_index}
                      onChange={e => setTableRef(i, { order_index: e.target.value })} sx={{ width: 90 }} />
                    <IconButton size="small" onClick={() => rmTableRef(i)} sx={{ color: '#c62828' }}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeDialog} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" onClick={submit} disabled={isLoading}
            sx={{ textTransform: 'none', backgroundColor: '#173E4B', '&:hover': { backgroundColor: '#0f2d38' } }}>
            {isLoading ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : (dialogMode === 'edit' ? 'Save Changes' : 'Create Node')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={Boolean(deleteConfirmGuid)} onClose={() => setDeleteConfirmGuid(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Rating Node?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This permanently deletes the rating node. Any benefit or add-on referencing its GUID loses the association.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button onClick={() => setDeleteConfirmGuid(null)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" color="error" onClick={doDelete} sx={{ textTransform: 'none' }}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RatingNodesTab;
